// ─────────────────────────────────────────────────────────────
// LinkModal — wizard for creating / editing a LinkDef.
//
// Step 1: pick mode (Populate / Remove)
// Step 2: configure
//   - Question + Task pickers (or formula-mode multi-task picker)
//   - MetaKey field (required, with mismatch detection)
//   - Trigger picker (for boolean / choice questions)
//   - Populate: simple passthrough OR condition→formula rules (IF/ELSE IF/ELSE)
//   - Remove: cascade toggle
// ─────────────────────────────────────────────────────────────

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  Typography, TextField, Select, MenuItem, FormControl, InputLabel, OutlinedInput, FormHelperText,
  FormControlLabel, Switch, IconButton, Checkbox, Card, Alert, Chip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import type {
  SDM, Task, LinkDef, LinkMode, DragSource, OID,
  TriggerOperator, ConditionClause, FormulaBranch, SaveOpts, TriggerValue,
} from '../types';
import { FORMULA_OPERATORS, SYSTEM_METAKEYS, SYSTEM_METAKEY_LIST, INSTALL_TASK_UNITS, isNonMappablePhase } from '../constants';
import { detectFormulaVars, resolvablePlanActionEntries } from '../expressions';
import { MetaKeyChip } from '../Bits';
import type { PlanMasterSummaryDTO } from '../../../automation/AutomationTypes';
// `fetchPlanMasters` (from AutomationMasterService) is imported DYNAMICALLY inside the plan-master
// effect below, not statically here — see that effect's comment for why.

export interface LinkModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (link: LinkDef, opts: SaveOpts) => void;
  sdm: SDM;
  tasks: Task[];
  taskById: Record<number, Task>;
  source: DragSource | null;
  targetTask: Task | null;
  editing: LinkDef | null;
  // All currently-known links (authored + reconstructed), used to guard Remove-link targetMetaKey
  // uniqueness — SdmCodeGen's applyRemoveLinkToSdm matches derived deletion flags by MetaKey alone,
  // so two Remove links sharing a targetMetaKey silently collapse into one at codegen time.
  existingLinks?: LinkDef[];
}

type Step = 'mode' | 'configure';

/**
 * The trigger operators that mean "fire when the answer is NOT this". The modal keeps one
 * polarity flag for every choice question and picks the concrete operator at save time:
 * multiselect uses contains/not_contains, single-select and boolean use eq/ne for one value
 * and in/not_in for several, since eq reads only the first value it is given.
 */
const NEGATED_TRIGGER_OPERATORS = new Set(['not_contains', 'ne', 'not_in']);

/**
 * `crypto.randomUUID()` is standard in every real deployment target (all modern browsers, Node
 * 19+) — but this project's CRA/Jest jsdom test environment does not polyfill `crypto`, which was
 * never triggered before because no prior test actually clicked "Create link" through to a full
 * save (LinkModal.switchPlan.test.tsx is the first to). Fall back to a non-cryptographic random
 * suffix only when `crypto`/`randomUUID` is unavailable — behavior in every real environment is
 * unchanged.
 */
function newLinkOidSuffix(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function LinkModal(props: LinkModalProps): JSX.Element {
  const { open, onClose, onSave, sdm, tasks, source, targetTask, editing, existingLinks = [] } = props;
  const tv = sdm.TemplateVersion[0];
  const allItems = tv.ItemDef;

  // ── State ──────────────────────────────────────────────
  const [step,            setStep]            = useState<Step>('mode');
  const [mode,            setMode]            = useState<Exclude<LinkMode, 'formula'>>('populate');
  const [questionOID,     setQuestionOID]     = useState<string>('');
  const [taskId,          setTaskId]          = useState<string>('');
  const [metaKey,         setMetaKey]         = useState<string>('');
  const [unit,            setUnit]            = useState<string>('');
  const [numericalMapping, setNumericalMapping] = useState<string>('');
  const [triggerValues,   setTriggerValues]   = useState<TriggerValue[]>([]);
  const [triggerOp,       setTriggerOp]       = useState<TriggerOperator>('eq');
  const [msDeleteOp,      setMsDeleteOp]      = useState<'not_contains' | 'contains'>('not_contains');
  const [populateField,   setPopulateField]   = useState<string>('additionalData.quantity');
  const [formulaTaskIds,  setFormulaTaskIds]  = useState<number[]>([]);

  const [useFormula,      setUseFormula]      = useState<boolean>(false);
  const [formulaName,     setFormulaName]     = useState<string>('');
  const [formulaVisible,  setFormulaVisible]  = useState<boolean>(false);
  const [branches,        setBranches]        = useState<FormulaBranch[]>([
    { conditions: [{ field: '', op: 'eq', values: [] }], formulaTemplate: '', formulaVars: {} },
  ]);
  const [defaultValue,    setDefaultValue]    = useState<string>('0');

  // Switch-Plan mode state.
  const [pickedPlanUid,      setPickedPlanUid]      = useState<string>('');
  const [chosenActionIndex,  setChosenActionIndex]  = useState<number>(0);

  // AB#26913-class 'Switch automation plan' link authoring — mirrors TaskInspector.tsx's EXACT
  // plan-master fetch pattern (PUBLISHED only).
  //
  // useOidcAccessToken() must be called unconditionally at the top level (react-hooks/rules-of-hooks
  // forbids wrapping a hook call in try/catch — a prior version did this to survive rendering
  // without a mounted <OidcProvider>). Every test that renders LinkModal now mocks
  // '@axa-fr/react-oidc' instead (LinkModal.test.tsx mocks it to return no token, matching this
  // file's pre-existing behavior since it never exercises Switch-Plan; LinkModal.switchPlan.test.tsx
  // and NaomiStudio.test.tsx mock it with a real token).
  //
  // `fetchPlanMasters` is still required LAZILY (synchronously, via `require`, not a static
  // top-level `import`), inside the effect, behind the `!accessToken` guard — so the module (and the
  // axios it transitively pulls in via AutomationMasterService -> services/ApiBase.ts) is never even
  // requested in a test where accessToken is never truthy (e.g. LinkModal.test.tsx, which mocks no
  // token and therefore never needs to also mock axios/AutomationMasterService). A static top-level
  // import would make CRA's Jest (babel-jest) attempt to parse axios's real ESM-only default
  // resolution path for EVERY test that merely renders this file, which fails. (`require`, not a
  // dynamic `import()`, deliberately — `import()` always resolves asynchronously, which shifted this
  // fetch by an extra tick and made LinkModal.switchPlan.test.tsx's `waitFor(fetchPlanMasters
  // called)` race the subsequent state update when run as part of the full suite.)
  const { accessToken } = useOidcAccessToken();
  const [planMasters, setPlanMasters] = useState<PlanMasterSummaryDTO[]>([]);
  useEffect(() => {
    // Gate on `open` too, not just `accessToken`: LinkModal is permanently mounted inside
    // MappingPane's JSX tree (rendered whether or not a link-authoring dialog is actually visible),
    // so an unconditional fetch here fires on EVERY MappingPane mount — including every
    // NaomiStudio test that reaches the editor view, none of which mock AutomationMasterService.
    // Only fetch the plan catalog when the modal is actually open (lazy-load on demand).
    if (!open || !accessToken) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { fetchPlanMasters } = require('../../../automation/AutomationMasterService') as
      typeof import('../../../automation/AutomationMasterService');
    fetchPlanMasters(accessToken).then((res) => {
      if (res.kind === 'ok') {
        setPlanMasters(res.data.filter((m) => m.status === 'PUBLISHED'));
      }
    });
  }, [open, accessToken]);

  // ── Reset state when the modal opens ──────────────────
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setStep('configure');
      setMode(editing.mode === 'formula' ? 'populate' : editing.mode);
      setQuestionOID(editing.sourceItemOID ?? '');
      setTaskId(String(editing.targetTaskIds[0] ?? ''));
      setMetaKey(editing.sourceMetaKey ?? editing.targetMetaKey ?? editing.populate?.formulaMetaKey ?? '');
      setTriggerValues(editing.trigger?.values ?? []);
      setTriggerOp(editing.trigger?.operator ?? 'eq');
      setMsDeleteOp(NEGATED_TRIGGER_OPERATORS.has(editing.trigger?.operator ?? '')
        ? 'not_contains' : 'contains');
      setPopulateField(editing.populate?.field ?? 'additionalData.quantity');
      setUseFormula(
        editing.mode === 'formula'
        || !!editing.populate?.formulaTemplate
        || !!editing.remove?.branches?.length,
      );
      setFormulaName(editing.populate?.formulaName ?? editing.remove?.formulaName ?? '');
      setFormulaVisible(editing.populate?.formulaVisible ?? editing.remove?.formulaVisible ?? false);
      setBranches(editing.populate?.branches ?? editing.remove?.branches ?? [
        { conditions: [{ field: '', op: 'eq', values: [] }], formulaTemplate: '', formulaVars: {} },
      ]);
      setDefaultValue(String(editing.populate?.defaultValue ?? '0'));
      setNumericalMapping(String(editing.populate?.numericValue ?? ''));
      // Pre-fill Unit from the link's stored unitTo, else fall back to the Unit already on the
      // target task (it lives on task.additionalData.unit after a publish) so editing an existing
      // populate/formula link shows the saved unit instead of an empty dropdown.
      const editTargetTask = tasks.find(t => String(t.id) === String(editing.targetTaskIds[0] ?? ''));
      const editTaskUnit = editTargetTask && typeof editTargetTask.additionalData === 'object'
        ? (editTargetTask.additionalData.unit ?? '') : '';
      setUnit(String(editing.populate?.unitTo ?? editTaskUnit ?? ''));
      setFormulaTaskIds(
        editing.mode === 'formula' || editing.remove?.branches?.length ? editing.targetTaskIds : [],
      );
      setPickedPlanUid(editing.switchPlan?.pickedPlanUid ?? '');
      setChosenActionIndex(editing.switchPlan?.actionIndex ?? 0);
    } else if (source && targetTask) {
      // Drag-and-drop still goes through the mode step so the user picks
      // Populate vs Remove; fields below are pre-filled from the drop.
      setStep('mode');
      setMode(source.value === false ? 'remove' : 'populate');
      setQuestionOID(source.itemOID);
      setTaskId(String(targetTask.id));
      const tmk = typeof targetTask.additionalData === 'object' ? targetTask.additionalData.metaKey : null;
      setMetaKey(tmk ?? '');
      const tunit = typeof targetTask.additionalData === 'object' ? targetTask.additionalData.unit : null;
      setUnit(tunit ?? '');
      setTriggerValues(source.value !== null ? [source.value] : []);
      setTriggerOp('eq');
      setMsDeleteOp('not_contains');
      setPopulateField('additionalData.quantity');
      setUseFormula(false);
      setFormulaName(''); setFormulaVisible(false);
      setBranches([{ conditions: [{ field: '', op: 'eq', values: [] }], formulaTemplate: '', formulaVars: {} }]);
      setDefaultValue('0');
      setNumericalMapping('');
      setFormulaTaskIds([]);
      setPickedPlanUid('');
      setChosenActionIndex(0);
    } else {
      setStep('mode');
      setMode('populate');
      setQuestionOID(''); setTaskId(''); setMetaKey(''); setUnit('');
      setTriggerValues([]); setTriggerOp('eq'); setMsDeleteOp('not_contains');
      setPopulateField('additionalData.quantity');
      setUseFormula(false);
      setFormulaName(''); setFormulaVisible(false);
      setBranches([{ conditions: [{ field: '', op: 'eq', values: [] }], formulaTemplate: '', formulaVars: {} }]);
      setDefaultValue('0');
      setNumericalMapping('');
      setFormulaTaskIds([]);
      setPickedPlanUid('');
      setChosenActionIndex(0);
    }
    // Key on stable identifiers (not the `tasks`/`editing`/`source`/`targetTask` object references)
    // so a `setTasks` re-render elsewhere in MappingPane while this modal is open doesn't silently
    // reset in-progress, unsaved form edits back to the original snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.OID, source?.itemOID, targetTask?.id]);

  // ── Derived values ────────────────────────────────────
  // Tasks eligible as a link target: never Sales-phase (non-mappable — sales scheduling runs before
  // mapping), and (populate/formula only) install tasks only. Feeds both the single-select Task
  // dropdown and the formula multi-task picker.
  // Turning formula mode on carries the task already chosen in the Task dropdown into the
  // formula's target list, so the author is not asked to pick the same task twice. Both
  // pickers are fed by mappableTasks, so the id is always one the list can render. An
  // existing selection is left alone, and turning the mode off keeps it for the way back.
  const toggleFormula = () => {
    const next = !useFormula;
    if (next) {
      const picked = Number(taskId);
      if (Number.isFinite(picked) && picked > 0) {
        setFormulaTaskIds(ids => (ids.length ? ids : [picked]));
      }
      // The question chosen above is the one the author is reasoning about, so it opens
      // the first IF row rather than leaving an empty picker they must fill in again.
      if (mode === 'remove' && !formulaName.trim()) {
        const mkNow = (enteredMK || '').trim();
        if (mkNow) setFormulaName(mkNow);
      }
      if (questionOID) {
        setBranches(prev => {
          const first = prev[0];
          if (!first || first.conditions.some(c => c.field)) return prev;
          return prev.map((b, i) => i !== 0 ? b : {
            ...b,
            conditions: b.conditions.map((c, ci) => ci !== 0 ? c : { ...c, field: questionOID }),
          });
        });
      }
    }
    setUseFormula(next);
  };

  const mappableTasks = useMemo(() => {
    const base = tasks.filter(t => !isNonMappablePhase(t.phase));
    if (mode === 'populate') return base.filter(t => t.taskType === 'Install Task');
    if (mode === 'switchPlan') return base.filter(t => resolvablePlanActionEntries(t).length > 0);
    return base;
  }, [tasks, mode]);
  const item       = useMemo(() => allItems.find(i => i.OID === questionOID) ?? null, [allItems, questionOID]);
  const task       = useMemo(() => tasks.find(t => String(t.id) === taskId) ?? null, [tasks, taskId]);
  // Switch-Plan: this task's qualifying (Start/End Automation Plan) actions, and the one the
  // author has picked among them (defaulting to the only/first one).
  const planActionChoices = useMemo(() => (task ? resolvablePlanActionEntries(task) : []), [task]);
  const chosenPlanAction = planActionChoices[chosenActionIndex] ?? planActionChoices[0];
  const fallbackPlanUid = chosenPlanAction?.action.automationPlanMasterUid ?? '';
  // The "Switch to this plan" picker offers EVERY PUBLISHED plan master, deliberately NOT
  // narrowed to plans already wired somewhere in this trade template.
  //
  // An earlier revision did scope it that way (to plans referenced by some task's action here),
  // which looked tidy but broke the feature's whole purpose: the destination is by definition a
  // plan the task is NOT currently using — e.g. "4.1 Collect Payment" ships wired to
  // "Completed Job-RM" and the link exists to switch it to "Completed Job -All Jobs", a plan that
  // appears nowhere else in the template. Scoping hid exactly the plans an author needs to pick.
  //
  // The opt-in gating that DOES apply lives on the task + "which action?" pickers above
  // (resolvablePlanActionEntries) — that governs which action may be rewritten, which is a
  // different question from which plan is a legal destination.
  const fallbackPlanName = planMasters.find(m => m.planMasterUid === fallbackPlanUid)?.name ?? fallbackPlanUid;
  const handleTaskIdChange = (v: string) => {
    setTaskId(v);
    if (mode === 'switchPlan') setChosenActionIndex(0);
  };
  const taskMK     = task && typeof task.additionalData === 'object' ? task.additionalData.metaKey ?? '' : '';
  const taskUnit   = task && typeof task.additionalData === 'object' ? task.additionalData.unit    ?? '' : '';
  const codeList   = item?.CodeListRef ? tv.CodeList.find(c => c.OID === item.CodeListRef!.CodeListOID) : null;

  const isBool       = item?.DataType === 'boolean';
  const isChoice     = item?.DataType === 'singleselect' || item?.DataType === 'multiselect';
  const isMultiselect = item?.DataType === 'multiselect';

  // Populate is only valid for install tasks fed by a numeric question.
  // (The dragged pill for a numeric question is the generic "value" pill whose
  //  source.value is null, so resolve numeric-ness from the question's DataType.)
  const sourceItem = source ? allItems.find(i => i.OID === source.itemOID) ?? null : null;
  const ddIsNumeric = !!sourceItem && ['integer', 'float'].includes(sourceItem.DataType);
  const ddIsInstall = targetTask?.taskType === 'Install Task';
  const populateAllowed = !source || !targetTask || (ddIsNumeric && ddIsInstall);

  const isNumericQuestion = item ? ['integer', 'float'].includes(item.DataType) : true;
  const showNumericalMappingField =
    mode === 'populate' && !useFormula && !!questionOID && !isNumericQuestion;

  // Remove-mode condition picker for non-choice questions (numeric or free text):
  // the user picks an operator (and threshold) that triggers the deletion.
  const isNumericItem = !!item && (item.DataType === 'integer' || item.DataType === 'float');
  const isTextItem    = item?.DataType === 'text';
  const showRemoveCondition = (mode === 'remove' || mode === 'switchPlan') && !useFormula && (isNumericItem || isTextItem);

  // Default the polarity to what each question type meant BEFORE the toggle covered it, so
  // adding the control changes no existing rule's behaviour: multiselect deletion has always
  // been "does NOT include" (the canonical job-type pattern), while a single-select or boolean
  // has always compiled to a plain eq. Only for a NEW link — an edit hydrates from the stored
  // operator and must not be overwritten here.
  useEffect(() => {
    if (editing || !item) return;
    setMsDeleteOp(isMultiselect ? 'not_contains' : 'contains');
  }, [editing, item, isMultiselect]);
  const opNeedsValue = (op: TriggerOperator) =>
    !['always', 'is_empty', 'is_not_empty'].includes(op);

  const itemMK     = item?.MetaKey ?? '';
  const enteredMK  = metaKey.trim();
  const hasMismatch    = !!(itemMK && taskMK && itemMK !== taskMK);
  const userMismatch   = !!(enteredMK && itemMK && enteredMK !== itemMK)
                      || !!(enteredMK && taskMK && enteredMK !== taskMK);
  const mkConflict     = hasMismatch || userMismatch;
  // Question-side MetaKey uniqueness: a populate link stamps enteredMK on the source question,
  // so it must not already be owned by a DIFFERENT real question. Derived items are exempt —
  // they are regenerated and intentionally keyed by their MetaKey.
  const mkOtherOwner   = (mode === 'populate' && !useFormula && enteredMK)
    ? allItems.find(i => i.MetaKey === enteredMK && i.OID !== questionOID && !i._derived) ?? null
    : null;
  const mkTakenByOther = !!mkOtherOwner;
  // Remove-link targetMetaKey uniqueness: applyRemoveLinkToSdm (SdmCodeGen.ts) matches a derived
  // deletion flag by MetaKey equality alone — two Remove links sharing a targetMetaKey silently
  // collapse into one derived ItemDef/MethodDef, overwriting one link's deletion rule with the
  // other's. Guard against entering a targetMetaKey already used by a DIFFERENT Remove link.
  const removeMkOtherOwner = (mode === 'remove' && enteredMK)
    ? existingLinks.find(l => l.mode === 'remove' && l.targetMetaKey === enteredMK
        && l.OID !== editing?.OID) ?? null
    : null;
  const removeMkTakenByOther = !!removeMkOtherOwner;
  // Switch-Plan MetaKey uniqueness: applySwitchPlanLinkToSdm (SdmCodeGen.ts) — like
  // applyRemoveLinkToSdm above — matches the existing derived item purely by MetaKey equality with
  // no `_derived` filter, so an unguarded collision silently collapses two links' rules into one, or
  // wires the switch-plan MethodDef onto an unrelated real question's OID. Guard against entering a
  // MetaKey already owned by an existing non-derived question (mirrors mkOtherOwner) or by a
  // DIFFERENT Switch-Plan link's own derived MetaKey (mirrors removeMkOtherOwner).
  const switchPlanMkOtherOwner = (mode === 'switchPlan' && enteredMK)
    ? (allItems.find(i => i.MetaKey === enteredMK && !i._derived) ?? null)
      ?? (existingLinks.find(l => l.mode === 'switchPlan' && l.targetMetaKey === enteredMK
          && l.OID !== editing?.OID) ?? null)
    : null;
  const switchPlanMkTakenByOther = !!switchPlanMkOtherOwner;
  // Switch-Plan MetaKey-collision guard: a task can carry only ONE metaKey at a time (§2 rule 5,
  // sdm_task_linking_rules.md). A Switch-Plan link always mints its OWN dedicated derived MetaKey,
  // so if the target task already has a metaKey NOT OWNED BY THIS LINK — regardless of what the
  // author types into the MetaKey field below — a Switch-Plan link cannot be created/saved for it.
  // Distinct from mkConflict / userMismatch above, which only fire on a TYPED conflict: this fires
  // on any pre-existing task metaKey that isn't this link's own.
  //
  // "This link's own" is `editing?.targetMetaKey` — NOT `effectiveMK` (which falls back to taskMK
  // whenever the author hasn't typed anything), because on a brand-new link (editing === null) that
  // fallback would make `taskMK !== effectiveMK` compare taskMK to itself and silently clear the
  // guard for exactly the untyped-field case this guard exists to catch — reopening an EXISTING
  // Switch-Plan link (editing.targetMetaKey === taskMK, since saving one always stamps its own
  // metaKey onto the task) is the only case this should exempt.
  const switchPlanTaskHasOtherMetaKey =
    mode === 'switchPlan' && !!taskMK && taskMK !== (editing?.targetMetaKey ?? null);
  const effectiveMK    = enteredMK || itemMK || taskMK;
  const metaKeyValid   = !!effectiveMK && !mkConflict && !mkTakenByOther && !removeMkTakenByOther
    && !switchPlanMkTakenByOther;

  // Unit is a required user input when populating a value into a task (populate mode, direct or
  // formula). It must be one of the canonical task units (INSTALL_TASK_UNITS) — i.e. picked from
  // the dropdown, not free text. Remove mode never populates a value, so no unit is needed there.
  const enteredUnit    = unit.trim();
  const effectiveUnit  = enteredUnit;
  const unitRequired   = mode === 'populate';
  const unitValid      = !unitRequired || INSTALL_TASK_UNITS.includes(enteredUnit);

  // Switch-Plan: a non-empty trigger value is required regardless of which trigger UI applies —
  // the numeric/text operator picker (showRemoveCondition) validates via opNeedsValue as usual;
  // the bool/choice/multiselect ToggleButton path has no operator concept, so at least one value
  // must be toggled on.
  const switchPlanTriggerValid = showRemoveCondition
    ? (!opNeedsValue(triggerOp) || String(triggerValues[0] ?? '').trim() !== '')
    : triggerValues.length > 0;

  const canSave = (useFormula
    ? !!formulaName.trim() && formulaTaskIds.length > 0
      && (mode === 'remove' || branches.every(b => b.formulaTemplate.trim())) && metaKeyValid
    : mode === 'switchPlan'
      ? !!questionOID && !!taskId && !!pickedPlanUid && metaKeyValid
        && !switchPlanTaskHasOtherMetaKey && switchPlanTriggerValid
      : !!questionOID && !!taskId && metaKeyValid
        && (!showNumericalMappingField || !!numericalMapping.trim())
        && (!showRemoveCondition || !opNeedsValue(triggerOp)
            || String(triggerValues[0] ?? '').trim() !== ''))
    && unitValid;

  // The concrete operator a choice question saves, from the polarity toggle. Multiselect keeps
  // contains/not_contains (the canonical M.7/M.8/M.9 shape). Single-select and boolean resolve to
  // eq/ne for one value and in/not_in for several — NOT eq, which reads only values[0] and would
  // silently ignore every option after the first.
  const choiceTriggerOperator: TriggerOperator = isMultiselect
    ? msDeleteOp
    : (triggerValues.length > 1
        ? (msDeleteOp === 'not_contains' ? 'not_in' : 'in')
        : (msDeleteOp === 'not_contains' ? 'ne' : 'eq'));

  // ── Save ──────────────────────────────────────────────
  const saveIt = () => {
    // Switch-Plan is authored + saved independently of the populate/remove/formula branching below
    // (it never uses the condition→formula builder, and its LinkDef shape — switchPlan config,
    // no populate/remove block — doesn't fit that flow).
    if (mode === 'switchPlan') {
      const mk = effectiveMK || null;
      // Same operator derivation the existing mode==='remove' branch below uses for a multiselect
      // trigger (msDeleteOp, not the numeric/text-only triggerOp) — see showRemoveCondition/isMultiselect
      // just above.
      const spOperator: TriggerOperator = showRemoveCondition
        ? triggerOp
        : ((isChoice || isBool) && triggerValues.length
            ? choiceTriggerOperator
            : (triggerValues.length ? 'eq' : 'always'));
      const spValues = showRemoveCondition && !opNeedsValue(triggerOp) ? [] : triggerValues;
      const link: LinkDef = {
        OID: editing?.OID ?? `L.${newLinkOidSuffix()}`,
        sourceItemOID: questionOID,
        sourceMetaKey: mk ?? undefined,
        targetTaskIds: taskId ? [Number(taskId)] : [],
        targetMetaKey: mk ?? undefined,
        mode: 'switchPlan',
        trigger: { operator: spOperator, values: spValues },
        switchPlan: {
          pickedPlanUid,
          // The fallback is READ from the task's own current action — never re-entered by the author.
          fallbackPlanUid,
          actionIndex: chosenPlanAction?.index ?? 0,
        },
      };
      onSave(link, {
        stampMetaKey: false,
        stampTaskMetaKey: !!mk,
        stampTaskMetaKeyValue: mk,
        stampTaskIds: taskId ? [Number(taskId)] : [],
        stampActionPlanResolution: taskId
          ? { taskId: Number(taskId), actionIndex: chosenPlanAction?.index ?? 0 }
          : undefined,
      });
      return;
    }

    const removeWithRules = mode === 'remove' && useFormula;
    const effectiveBranches = removeWithRules
      ? branches.map(b => ({ ...b, formulaTemplate: 'true' }))
      : branches;
    // A Remove link stays in 'remove' mode even when the condition builder is used — only
    // populate links become 'formula' (a value-computing decision table). Conflating the two
    // is what made a delete rule show up (and behave) as a populate formula.
    const finalMode: LinkMode = (useFormula && mode !== 'remove') ? 'formula' : mode;
    // Use the EFFECTIVE MetaKey (typed value, else the question's existing ItemDef key, else the
    // task's key) — the same value the validation and the "Will stamp …" preview use. Reading only
    // enteredMK here meant that when the question already carried a MetaKey and the author didn't
    // retype it, nothing was stamped on the task despite the preview promising it would be.
    const mk = effectiveMK || null;
    const finalTargetIds = useFormula
      ? formulaTaskIds
      : (taskId ? [Number(taskId)] : []);
    // For a remove rule built via conditions, anchor the link to the first condition's question
    // (when no question was pre-selected) so it groups sensibly and codegen can locate its group.
    const effectiveSourceOID = removeWithRules && !questionOID
      ? (effectiveBranches[0]?.conditions?.find(c => c.field)?.field ?? '')
      : questionOID;

    // Numeric / free-text remove links carry the chosen operator; everything else
    // keeps the original eq / always behavior.
    // Multiselect deletion uses contains/not_contains (the canonical M.7/M.8/M.9 shape);
    // numeric/text use the operator picker; everything else falls back to eq / always.
    const triggerOperator: TriggerOperator = showRemoveCondition
      ? triggerOp
      : (mode === 'remove' && (isChoice || isBool) && triggerValues.length
          ? choiceTriggerOperator
          : (triggerValues.length ? 'eq' : 'always'));
    const triggerVals = showRemoveCondition && !opNeedsValue(triggerOp) ? [] : triggerValues;

    const link: LinkDef = {
      // crypto.randomUUID() (not Date.now()) — a millisecond-resolution timestamp let two links
      // saved within the same millisecond (e.g. a rapid double-click) collide on OID, and
      // MappingPane.saveLink's findIndex(l => l.OID === link.OID) would then treat the second
      // save as an edit of the first, silently losing one link.
      OID: editing?.OID ?? `L.${crypto.randomUUID()}`,
      sourceItemOID: effectiveSourceOID || undefined,
      sourceMetaKey: mk ?? undefined,
      targetTaskIds: finalTargetIds,
      targetMetaKey: mk ?? taskMK ?? undefined,
      mode: finalMode,
      trigger: { operator: triggerOperator, values: triggerVals },
      ...(finalMode === 'populate' ? {
        populate: {
          field: populateField,
          // A non-numeric question maps its answer to the entered number; a numeric question
          // passes its own value through.
          expression: showNumericalMappingField && numericalMapping.trim()
            ? String(Number(numericalMapping.trim()) || 0)
            : `parseInt(${questionOID})||0`,
          ...(showNumericalMappingField && numericalMapping.trim()
            ? { numericValue: numericalMapping.trim() }
            : {}),
          unitTo: effectiveUnit || undefined,
        },
      } : {}),
      ...(finalMode === 'formula' ? {
        populate: {
          field: populateField,
          expression: '/* decision table */',
          formulaName,
          formulaMetaKey: mk ?? undefined,
          formulaVisible,
          branches: effectiveBranches,
          defaultValue,
          unitTo: effectiveUnit || undefined,
        },
      } : {}),
      ...(finalMode === 'remove' ? {
        remove: {
          cascadeDependents: false,
          // Carry the condition builder's branches so codegen can build the deletion expression.
          ...(removeWithRules ? { branches: effectiveBranches, formulaName, formulaVisible } : {}),
        },
      } : {}),
    };

    const opts: SaveOpts = {
      // Remove-mode links must not stamp MetaKey on the source question.
      // The source question is a trigger only; MetaKey belongs on the derived item.
      stampMetaKey: finalMode !== 'remove' && !!(mk && !itemMK),
      stampMetaKeyValue: mk,
      stampTaskMetaKey: !!mk,
      stampTaskMetaKeyValue: mk,
      // Populate links carry the user-entered Unit onto the target install task(s).
      stampTaskUnit: finalMode !== 'remove' && !!effectiveUnit,
      stampTaskUnitValue: effectiveUnit || null,
      stampTaskIds: finalTargetIds,
      // Populate formula links mint a live derived ItemDef here (and rebind the link to it).
      ...(useFormula && mode !== 'remove' ? {
        createDerivedItem: { name: formulaName, metaKey: mk, visible: formulaVisible },
      } : {}),
      // Remove links create their derived deletion-flag item in the live SDM too, so it shows as a
      // "(Derived) …" question immediately (no publish/reload needed) — matching formula links. The
      // MethodDef + ItemRef are still wired at sync by applyRemoveLinkToSdm (which reuses this item
      // by MetaKey). No source rebind — a remove link's source stays its trigger question.
      ...(mode === 'remove' && mk ? {
        createRemoveDerivedItem: { metaKey: mk },
      } : {}),
    };
    onSave(link, opts);
  };

  // ── Step 1: pick mode ─────────────────────────────────
  if (open && step === 'mode') {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LinkIcon color="primary" />
            <Box>
              <Typography variant="h6">Create a new link</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                What do you want this link to do?
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {([
              { id: 'populate' as const, label: 'Populate values',
                desc: 'Pass answer values into a task — with optional condition → formula rules',
                icon: <CalculateIcon />, color: 'success.main' as const, bg: 'success.50' as const },
              { id: 'remove' as const, label: 'Remove task',
                desc: 'Delete a task when an answer matches a condition',
                icon: <DeleteIcon />, color: 'error.main' as const, bg: 'error.50' as const },
              { id: 'switchPlan' as const, label: 'Switch automation plan',
                desc: 'Point a Start/End Automation Plan action at a different plan when an answer matches',
                icon: <SwapHorizIcon />, color: 'info.main' as const, bg: 'info.50' as const },
            ].filter(opt => opt.id !== 'populate' || populateAllowed)).map(opt => (
              <Card
                key={opt.id} variant="outlined"
                sx={{
                  p: 2, cursor: 'pointer', transition: 'all .12s',
                  '&:hover': { borderColor: opt.color, bgcolor: opt.bg },
                }}
                onClick={() => {
                  setMode(opt.id);
                  // Switch-Plan's natural default reads "switch WHEN the selected option(s) are
                  // chosen" — the opposite of Remove's "delete when NOT chosen" default — for the
                  // shared multiselect includes/excludes toggle (msDeleteOp).
                  if (opt.id === 'switchPlan') setMsDeleteOp('contains');
                  setStep('configure');
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 1.25,
                    bgcolor: opt.bg, color: opt.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {opt.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{opt.label}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{opt.desc}</Typography>
                  </Box>
                </Stack>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Step 2: configure ─────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          {mode === 'populate' ? <CalculateIcon color="success" />
            : mode === 'switchPlan' ? <SwapHorizIcon color="info" />
            : <DeleteIcon color="error" />}
          <Box>
            <Typography variant="h6">
              {mode === 'populate' ? 'Populate task values'
                : mode === 'switchPlan' ? 'Switch automation plan'
                : 'Remove task'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {item && task
                ? `"${item.Question.TranslatedText.text}" → ${task.taskName}`
                : 'Configure the link below'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Question + Task pickers (hidden when formula mode is on) */}
        {!useFormula && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="lm-question-label" shrink>Question</InputLabel>
              <Select
                labelId="lm-question-label"
                input={<OutlinedInput notched label="Question" size="small" />}
                value={questionOID}
                onChange={e => setQuestionOID(e.target.value as string)}
                displayEmpty
              >
                <MenuItem value=""><em>— Select question —</em></MenuItem>
                {allItems
                  .filter(i => mode === 'populate' ? ['integer', 'float', 'text'].includes(i.DataType) : true)
                  .map(i => (
                    <MenuItem key={i.OID} value={i.OID}>
                      {i.Question.TranslatedText.text}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Box sx={{ color: 'primary.main', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
              &rarr;
            </Box>

            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="lm-task-label" shrink>Task</InputLabel>
              <Select
                labelId="lm-task-label"
                input={<OutlinedInput notched label="Task" size="small" />}
                value={taskId}
                onChange={e => handleTaskIdChange(e.target.value as string)}
                displayEmpty
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 300 } },
                  disableScrollLock: true,
                }}
              >
                <MenuItem value=""><em>— Select task —</em></MenuItem>
                {mappableTasks.map(t => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    #{t.id} {t.taskName}
                  </MenuItem>
                ))}
              </Select>
              {mode === 'switchPlan' && (
                <FormHelperText>
                  {mappableTasks.length === 0
                    ? 'No eligible tasks. Switch on "Resolve plan from this task\'s own MetaKey" for the '
                      + 'task\'s automation-plan action in the Builder\'s Actions accordion first.'
                    : 'Only tasks whose automation-plan action has "Resolve plan from this task\'s own '
                      + 'MetaKey" switched on are listed.'}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        )}

        {/* Numerical mapping when a non-numeric question feeds a populate link */}
        {showNumericalMappingField && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>&ldquo;{item?.Question.TranslatedText.text}&rdquo;</strong> is not a numeric
              question. What numerical value should be used for this answer?
            </Typography>
            <TextField
              size="small"
              label="Numerical value"
              value={numericalMapping}
              onChange={e => setNumericalMapping(e.target.value)}
              placeholder="e.g. 1"
              inputProps={{ type: 'number', style: { fontFamily: 'JetBrains Mono, monospace' } }}
              sx={{ width: 160 }}
            />
          </Alert>
        )}

        {/* Formula-mode multi-task picker */}
        {useFormula && (
          <Card variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'secondary.50', borderColor: 'secondary.light' }}>
            <Typography variant="overline" sx={{ color: 'secondary.dark', fontWeight: 700, display: 'block' }}>
              {mode === 'remove' ? 'Tasks this rule removes'
                : mode === 'switchPlan' ? 'Tasks this rule switches'
                : 'Tasks this formula populates'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              {mode === 'remove'
                ? 'The rule below decides whether these tasks stay. Pick the tasks it applies to.'
                : mode === 'switchPlan'
                  ? 'The rule below decides which automation plan these tasks start. Pick the tasks it applies to.'
                  : 'The formula becomes a new derived question. Pick which install tasks consume its value.'}
            </Typography>
            <Box sx={{
              maxHeight: 180, overflow: 'auto',
              bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1,
            }}>
              {mappableTasks.map(t => {
                const sel = formulaTaskIds.includes(t.id);
                const tmk = typeof t.additionalData === 'object' ? t.additionalData.metaKey : null;
                return (
                  <Stack
                    key={t.id}
                    direction="row" alignItems="center" spacing={1}
                    sx={{
                      px: 1, py: 0.5, cursor: 'pointer',
                      borderBottom: 1, borderColor: 'divider',
                      bgcolor: sel ? 'secondary.50' : 'transparent',
                    }}
                    onClick={() => setFormulaTaskIds(p =>
                      sel ? p.filter(id => id !== t.id) : [...p, t.id],
                    )}
                  >
                    <Checkbox size="small" checked={sel} />
                    <Typography variant="caption" sx={{
                      fontFamily: 'JetBrains Mono, monospace', color: 'text.disabled', minWidth: 28,
                    }}>#{t.id}</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{t.taskName}</Typography>
                    {tmk && <MetaKeyChip value={tmk} dim />}
                  </Stack>
                );
              })}
            </Box>
            {mode === 'remove' && formulaTaskIds.length > 1 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                These tasks all carry the MetaKey below, so one rule governs them as a group —
                they are removed together or not at all.
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.75 }}>
              {formulaTaskIds.length === 0
                ? (mode === 'remove'
                    ? 'No tasks selected — pick at least one task for this rule to remove.'
                    : 'No tasks selected — pick at least one.')
                : `${formulaTaskIds.length} task${formulaTaskIds.length === 1 ? '' : 's'} selected`}
            </Typography>
          </Card>
        )}

        {/* Direct populate summary — shown when question + task selected and no formula */}
        {mode === 'populate' && !useFormula && item && task && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The answer to{' '}
            <strong>&ldquo;{item.Question.TranslatedText.text}&rdquo;</strong>
            {' '}will be applied to task{' '}
            <strong>#{task.id} &ldquo;{task.taskName}&rdquo;</strong>
            {' '}via MetaKey{' '}
            <Box component="code" sx={{
              fontFamily: 'JetBrains Mono, monospace',
              bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5,
            }}>
              {effectiveMK || '(set below)'}
            </Box>
          </Alert>
        )}

        {/* MetaKey field */}
        <Card variant="outlined" sx={{
          p: 1.25, mb: 2,
          borderColor: mkConflict ? 'error.main' : 'divider',
          bgcolor: mkConflict ? 'error.50' : 'action.hover',
        }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="overline" sx={{
              fontWeight: 700, color: mkConflict ? 'error.main' : 'text.secondary',
            }}>
              MetaKey <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              size="small" fullWidth
              value={metaKey}
              onChange={e => setMetaKey(e.target.value)}
              // Switch-Plan always needs its OWN dedicated derived MetaKey (see the collision guard
              // below) — never suggest the task's pre-existing key as a fill-in-the-blank hint here,
              // that would point the author at exactly the key they can't reuse.
              placeholder={itemMK || (mode !== 'switchPlan' ? taskMK : '') || 'Enter a MetaKey...'}
              error={mkConflict}
              inputProps={{ list: 'naomi-mk-suggest', style: { fontFamily: 'JetBrains Mono, monospace' } }}
            />
            {itemMK && !enteredMK && (
              <Button size="small" variant="outlined" onClick={() => setMetaKey(itemMK)}>
                use "{itemMK}"
              </Button>
            )}
            {mode !== 'switchPlan' && taskMK && !enteredMK && taskMK !== itemMK && (
              <Button size="small" variant="outlined" onClick={() => setMetaKey(taskMK)}>
                use "{taskMK}"
              </Button>
            )}
          </Stack>
          {hasMismatch && (
            <Alert severity="error" sx={{ mt: 1 }}>
              <b>MetaKey mismatch.</b> Question is "<b>{itemMK}</b>" but task is "<b>{taskMK}</b>". Edit one of them to match before linking.
            </Alert>
          )}
          {!hasMismatch && userMismatch && (
            <Alert severity="error" sx={{ mt: 1 }}>
              You entered "<b>{enteredMK}</b>" but {itemMK
                ? <>the question already uses "<b>{itemMK}</b>"</>
                : <>the task already uses "<b>{taskMK}</b>"</>}. Use that key, or edit the existing one.
            </Alert>
          )}
          {!mkConflict && mkTakenByOther && (
            <Alert severity="error" sx={{ mt: 1 }}>
              MetaKey "<b>{enteredMK}</b>" already belongs to another question
              (<b>{mkOtherOwner?.Question?.TranslatedText?.text ?? mkOtherOwner?.OID}</b>). Each
              MetaKey can be owned by only one question — pick a unique key.
            </Alert>
          )}
          {!mkConflict && removeMkTakenByOther && (
            <Alert severity="error" sx={{ mt: 1 }}>
              MetaKey "<b>{enteredMK}</b>" is already used by another Remove link. Two Remove
              links can't share a MetaKey — one would silently overwrite the other's deletion
              rule. Pick a unique key.
            </Alert>
          )}
          {!mkConflict && switchPlanMkTakenByOther && (
            <Alert severity="error" sx={{ mt: 1 }}>
              MetaKey "<b>{enteredMK}</b>" is already used by{' '}
              {(switchPlanMkOtherOwner as LinkDef)?.mode === 'switchPlan'
                ? 'another Switch-Plan link'
                : <>question "<b>{(switchPlanMkOtherOwner as { Question?: { TranslatedText?: { text?: string } } })
                    ?.Question?.TranslatedText?.text ?? (switchPlanMkOtherOwner as { OID?: string })?.OID}</b></>}.
              Each MetaKey can be owned by only one link/question — pick a unique key.
            </Alert>
          )}
          {!mkConflict && !mkTakenByOther && taskMK && !itemMK && questionOID && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This task is already linked to MetaKey "<b>{taskMK}</b>". Saving will stamp the question with the same key.
            </Alert>
          )}
          {!mkConflict && !mkTakenByOther && itemMK && !taskMK && taskId && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Will stamp "<b>{itemMK}</b>" on the task.
            </Alert>
          )}
          {!mkConflict && !mkTakenByOther && !itemMK && !taskMK && enteredMK && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Will stamp "<b>{enteredMK}</b>" on both the question and the task.
            </Alert>
          )}
        </Card>

        {/* Unit field — required to populate a value into an install task. Stamped on the task so
            ASE knows the unit of the populated quantity. Only relevant for populate (not remove). */}
        {mode === 'populate' && (
          <Card variant="outlined" sx={{
            p: 1.25, mb: 2,
            borderColor: unitValid ? 'divider' : 'error.main',
            bgcolor: unitValid ? 'action.hover' : 'error.50',
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="overline" sx={{
                fontWeight: 700, color: unitValid ? 'text.secondary' : 'error.main',
              }}>
                Unit <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <FormControl size="small" fullWidth error={!unitValid}>
                <Select
                  value={INSTALL_TASK_UNITS.includes(unit) ? unit : ''}
                  onChange={e => setUnit(e.target.value as string)}
                  displayEmpty
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value=""><em>— Select unit —</em></MenuItem>
                  {INSTALL_TASK_UNITS.map(u => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {!unitValid && (
              <Alert severity="error" sx={{ mt: 1 }}>
                A <b>Unit</b> is required to populate a value into an install task.
              </Alert>
            )}
            {unitValid && enteredUnit && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Will stamp unit "<b>{enteredUnit}</b>" on the task.
              </Alert>
            )}
          </Card>
        )}

        {/* Trigger picker for boolean / choice */}
        {item && !useFormula && (isBool || isChoice) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              When "{item.Question.TranslatedText.text}" is...
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {isBool && [{ v: true, label: 'Yes' }, { v: false, label: 'No' }].map(o => {
                const sel = triggerValues.includes(o.v);
                return (
                  <ToggleButton
                    key={String(o.v)} value={o.v} selected={sel} size="small"
                    onClick={() => setTriggerValues(sel
                      ? triggerValues.filter(v => v !== o.v)
                      : [...triggerValues, o.v])}
                  >
                    {o.label}
                  </ToggleButton>
                );
              })}
              {isChoice && codeList?.CodeListItem.map(ci => {
                const sel = triggerValues.includes(ci.CodedValue);
                return (
                  <ToggleButton
                    key={ci.CodedValue} value={ci.CodedValue} selected={sel} size="small"
                    onClick={() => setTriggerValues(sel
                      ? triggerValues.filter(v => v !== ci.CodedValue)
                      : [...triggerValues, ci.CodedValue])}
                  >
                    {ci.Decode.TranslatedText.text}
                  </ToggleButton>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Choice trigger polarity: match when NOT selected vs selected. Every choice question
            gets this — multiselect compiles to contains/not_contains, single-select and boolean
            to eq/ne (one value) or in/not_in (several). */}
        {(mode === 'remove' || mode === 'switchPlan') && !useFormula && (isChoice || isBool) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {mode === 'switchPlan' ? 'Switch the plan when the answer...' : 'Delete the task when the answer...'}
            </Typography>
            <ToggleButtonGroup
              exclusive size="small" value={msDeleteOp}
              onChange={(_, v) => { if (v) setMsDeleteOp(v as 'not_contains' | 'contains'); }}
              sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5 }}
            >
              <ToggleButton value="not_contains">
                {isMultiselect ? 'does NOT include the selected option(s)' : 'is NOT the selected option(s)'}
              </ToggleButton>
              <ToggleButton value="contains">
                {isMultiselect ? 'includes the selected option(s)' : 'is the selected option(s)'}
              </ToggleButton>
            </ToggleButtonGroup>
            {mode === 'remove' && (
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
                "Does NOT include" matches the canonical job-type pattern (delete a category's tasks when it wasn't chosen).
              </Typography>
            )}
          </Box>
        )}

        {/* Remove/Switch-Plan condition picker for numeric / free-text questions */}
        {showRemoveCondition && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {mode === 'switchPlan' ? 'Switch the plan' : 'Delete this task'} when "{item?.Question.TranslatedText.text}" is...
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <Select
                  value={triggerOp}
                  onChange={e => setTriggerOp(e.target.value as TriggerOperator)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } }, disableScrollLock: true }}
                >
                  {isNumericItem ? [
                    <MenuItem key="gt" value="gt">greater than (&gt;)</MenuItem>,
                    <MenuItem key="gte" value="gte">at least (&ge;)</MenuItem>,
                    <MenuItem key="lt" value="lt">less than (&lt;)</MenuItem>,
                    <MenuItem key="lte" value="lte">at most (&le;)</MenuItem>,
                    <MenuItem key="eq" value="eq">equals (=)</MenuItem>,
                    <MenuItem key="ne" value="ne">does not equal (&ne;)</MenuItem>,
                    <MenuItem key="is_empty" value="is_empty">is empty</MenuItem>,
                    <MenuItem key="is_not_empty" value="is_not_empty">is answered</MenuItem>,
                  ] : [
                    <MenuItem key="eq" value="eq">equals</MenuItem>,
                    <MenuItem key="ne" value="ne">does not equal</MenuItem>,
                    <MenuItem key="contains" value="contains">contains</MenuItem>,
                    <MenuItem key="not_contains" value="not_contains">does not contain</MenuItem>,
                    <MenuItem key="is_empty" value="is_empty">is empty</MenuItem>,
                    <MenuItem key="is_not_empty" value="is_not_empty">is answered</MenuItem>,
                  ]}
                </Select>
              </FormControl>
              {opNeedsValue(triggerOp) && (
                <TextField
                  size="small"
                  label="Value"
                  value={String(triggerValues[0] ?? '')}
                  onChange={e => setTriggerValues([e.target.value])}
                  placeholder={isNumericItem ? 'e.g. 5' : 'e.g. value'}
                  inputProps={isNumericItem
                    ? { type: 'number', style: { fontFamily: 'JetBrains Mono, monospace' } }
                    : { style: { fontFamily: 'JetBrains Mono, monospace' } }}
                  sx={{ width: 160 }}
                />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
              {mode === 'switchPlan'
                ? "The plan is switched when the customer's answer matches this condition."
                : "The task is deleted when the customer's answer matches this condition."}
            </Typography>
          </Box>
        )}

        {/* Switch-Plan mode: action picker (only shown when the task has more than one qualifying
            action) + the plan-master picker + the read-only fallback (the task's CURRENT
            automationPlanMasterUid on that action — never re-entered by the author) + the
            MetaKey-collision guard. */}
        {mode === 'switchPlan' && (
          <Box sx={{ mb: 2 }}>
            {switchPlanTaskHasOtherMetaKey && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                This task already has MetaKey "<b>{taskMK}</b>" — a Switch-Plan link needs its own
                dedicated MetaKey, and a task can only carry one. Remove the existing link/MetaKey
                from this task first.
              </Alert>
            )}

            {planActionChoices.length > 1 && (
              <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                <InputLabel id="lm-action-label" shrink>Which action?</InputLabel>
                <Select
                  labelId="lm-action-label"
                  input={<OutlinedInput notched label="Which action?" size="small" />}
                  value={String(chosenActionIndex)}
                  onChange={e => setChosenActionIndex(Number(e.target.value))}
                >
                  {planActionChoices.map(({ index, action }) => (
                    <MenuItem key={index} value={String(index)}>
                      {action.state ?? '—'} &rarr; {action.action ?? '—'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
              Falls back to:{' '}
              {fallbackPlanUid ? (
                <b>{fallbackPlanName}</b>
              ) : (
                <Box component="span" sx={{ fontStyle: 'italic' }}>
                  (no plan currently set on this action)
                </Box>
              )}
            </Alert>

            <FormControl size="small" fullWidth>
              <InputLabel id="lm-plan-label" shrink>Switch to this plan</InputLabel>
              <Select
                labelId="lm-plan-label"
                input={<OutlinedInput notched label="Switch to this plan" size="small" />}
                value={pickedPlanUid}
                onChange={e => setPickedPlanUid(e.target.value as string)}
                displayEmpty
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } }, disableScrollLock: true }}
              >
                <MenuItem value=""><em>— Select automation plan —</em></MenuItem>
                {planMasters.map(m => (
                  <MenuItem key={m.planMasterUid} value={m.planMasterUid}>
                    {m.name ?? m.planMasterUid}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Every published automation plan is selectable — the plan you switch TO is normally
                one this template does not use yet.
              </FormHelperText>
            </FormControl>
          </Box>
        )}

        {/* Populate mode */}
        {mode === 'populate' && (
          <Box sx={{ mb: 2 }}>
            {!useFormula && item && (
              <Alert severity="success" icon={<CalculateIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
                Set <b>quantity</b> = answer to <b>"{item.Question.TranslatedText.text}"</b> as a whole number
              </Alert>
            )}
            <Button
              variant={useFormula ? 'contained' : 'outlined'}
              startIcon={<AutoFixHighIcon />}
              onClick={toggleFormula}
              sx={{
                mb: useFormula ? 1.5 : 0,
                ...(useFormula
                  ? { bgcolor: '#1e40af', color: '#fff', '&:hover': { bgcolor: '#1e3a8a' } }
                  : { color: '#1e40af', borderColor: '#93c5fd', '&:hover': { borderColor: '#1e40af', bgcolor: '#eff6ff' } }),
              }}
            >
              {useFormula ? '✓ Using condition → formula rules' : '+ Add condition → formula rules'}
            </Button>

            {useFormula && (
              <FormulaPanel
                allItems={allItems}
                tv={tv}
                formulaName={formulaName} setFormulaName={setFormulaName}
                formulaVisible={formulaVisible} setFormulaVisible={setFormulaVisible}
                branches={branches} setBranches={setBranches}
                defaultValue={defaultValue} setDefaultValue={setDefaultValue}
              />
            )}
          </Box>
        )}

        {/* Remove mode */}
        {mode === 'remove' && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant={useFormula ? 'contained' : 'outlined'}
              startIcon={<AutoFixHighIcon />}
              onClick={toggleFormula}
              sx={{
                mb: useFormula ? 1.5 : 0,
                ...(useFormula
                  ? { bgcolor: '#1e40af', color: '#fff', '&:hover': { bgcolor: '#1e3a8a' } }
                  : { color: '#1e40af', borderColor: '#93c5fd', '&:hover': { borderColor: '#1e40af', bgcolor: '#eff6ff' } }),
              }}
            >
              {useFormula ? '✓ Using condition → formula rules' : '+ Add condition → formula rules'}
            </Button>

            {useFormula && (
              <FormulaPanel
                allItems={allItems}
                tv={tv}
                formulaName={formulaName} setFormulaName={setFormulaName}
                formulaVisible={formulaVisible} setFormulaVisible={setFormulaVisible}
                branches={branches} setBranches={setBranches}
                defaultValue={defaultValue} setDefaultValue={setDefaultValue}
                removeMode={true}
                removeTaskCount={formulaTaskIds.length}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('mode')}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={saveIt}>
          {editing ? 'Update link' : 'Create link'}
        </Button>
      </DialogActions>

      {/* MetaKey autocomplete suggestions */}
      <datalist id="naomi-mk-suggest">
        {SYSTEM_METAKEY_LIST.slice().sort().map(mk => <option key={mk} value={mk} />)}
      </datalist>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// FormulaPanel — branch editor
// ─────────────────────────────────────────────────────────────

/** What one branch of a populate decision table does when its conditions match. The generator
 *  writes the chosen value straight into the IF, so these ARE the stored literals: a quantity
 *  expression, `true` (ASE removes the task) or `null` (ASE leaves it alone). */
type BranchOutcome = 'value' | 'true' | 'null';

const branchOutcome = (template: string | undefined): BranchOutcome => {
  const t = (template ?? '').trim();
  return t === 'true' || t === 'null' ? t : 'value';
};

interface FormulaPanelProps {
  allItems: SDM['TemplateVersion'][0]['ItemDef'];
  tv: SDM['TemplateVersion'][0];
  formulaName: string;
  setFormulaName: (s: string) => void;
  formulaVisible: boolean;
  setFormulaVisible: (v: boolean) => void;
  branches: FormulaBranch[];
  setBranches: React.Dispatch<React.SetStateAction<FormulaBranch[]>>;
  defaultValue: string;
  setDefaultValue: (s: string) => void;
  removeMode?: boolean;
  removeTaskCount?: number;
}

function FormulaPanel({
  allItems, tv, formulaName, setFormulaName,
  formulaVisible, setFormulaVisible, branches, setBranches,
  defaultValue, setDefaultValue,
  removeMode,
  removeTaskCount = 0,
}: FormulaPanelProps): JSX.Element {
  const numericItems = allItems.filter(i => ['integer', 'float', 'text', 'boolean'].includes(i.DataType));

  return (
    <Card variant="outlined" sx={{ p: 1.5, bgcolor: 'secondary.50', borderColor: 'secondary.light' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField
          size="small" label="Formula name *"
          value={formulaName}
          onChange={e => setFormulaName(e.target.value)}
          placeholder="e.g. Total Roof Area"
          sx={{ flex: 1 }}
        />
        <FormControlLabel
          control={<Switch checked={formulaVisible} onChange={(_, v) => setFormulaVisible(v)} />}
          label="Visible"
        />
      </Stack>

      {removeMode && (
        <Typography variant="body2" sx={{ mb: 1, color: 'error.dark', fontWeight: 600 }}>
          {removeTaskCount === 1
            ? 'Remove the 1 ticked task when ANY of these is true — otherwise this rule asks for nothing:'
            : `Remove the ${removeTaskCount} ticked tasks when ANY of these is true — otherwise this rule asks for nothing:`}
        </Typography>
      )}
      <Stack spacing={1.25}>
        {branches.map((br, bi) => {
          const brVars = detectFormulaVars(br.formulaTemplate);
          return (
            <Card key={bi} variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper' }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 700, color: 'secondary.dark' }}>
                  {removeMode ? (bi === 0 ? 'When' : 'Or when') : (bi === 0 ? 'IF' : 'ELSE IF')}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {branches.length > 1 && (
                  <IconButton size="small" color="error"
                    onClick={() => setBranches(prev => prev.filter((_, i) => i !== bi))}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>

              {/* Conditions (AND) */}
              <Box sx={{ pl: 1, borderLeft: 2, borderColor: 'secondary.light', mb: 1 }}>
                <Typography variant="caption" sx={{
                  display: 'block', fontWeight: 600,
                  color: 'secondary.dark', textTransform: 'uppercase', mb: 0.5,
                }}>
                  Condition
                </Typography>
                {br.conditions.map((cond, ci) => {
                  const cItem = cond.field ? allItems.find(i => i.OID === cond.field) : null;
                  const cCL = cItem?.CodeListRef ? tv.CodeList.find(c => c.OID === cItem.CodeListRef!.CodeListOID) : null;
                  return (
                    <Stack key={ci} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                      {ci > 0 && (
                        <ToggleButtonGroup
                          exclusive size="small"
                          value={cond.connector ?? 'AND'}
                          onChange={(_, v) => { if (!v) return; setBranches(prev => prev.map((b, i) => i !== bi ? b : {
                            ...b, conditions: b.conditions.map((c, j) => j === ci ? { ...c, connector: v as 'AND' | 'OR' } : c),
                          })); }}
                          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: 11, fontWeight: 700 } }}
                        >
                          <ToggleButton value="AND">AND</ToggleButton>
                          <ToggleButton value="OR">OR</ToggleButton>
                        </ToggleButtonGroup>
                      )}
                      <Select
                        size="small" displayEmpty
                        value={cond.field}
                        onChange={e => setBranches(prev => prev.map((b, i) => i !== bi ? b : {
                          ...b, conditions: b.conditions.map((c, j) => j === ci
                            ? { ...c, field: e.target.value as OID, values: [] }
                            : c),
                        }))}
                        sx={{ minWidth: 180, fontSize: 12 }}
                      >
                        <MenuItem value="">— Question —</MenuItem>
                        {allItems.map(i => (
                          <MenuItem key={i.OID} value={i.OID}>{i.Question.TranslatedText.text}</MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={cond.op}
                        onChange={e => setBranches(prev => prev.map((b, i) => i !== bi ? b : {
                          ...b, conditions: b.conditions.map((c, j) => j === ci
                            ? { ...c, op: e.target.value as TriggerOperator }
                            : c),
                        }))}
                        sx={{ fontSize: 12 }}
                      >
                        <MenuItem value="eq">is</MenuItem>
                        <MenuItem value="ne">is not</MenuItem>
                        <MenuItem value="gt">&gt;</MenuItem>
                        <MenuItem value="gte">≥</MenuItem>
                        <MenuItem value="lt">&lt;</MenuItem>
                        <MenuItem value="lte">≤</MenuItem>
                        <MenuItem value="contains">includes</MenuItem>
                        <MenuItem value="not_contains">excludes</MenuItem>
                      </Select>
                      {cCL ? (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {cCL.CodeListItem.map(ci2 => {
                            const sel = cond.values.includes(ci2.CodedValue);
                            return (
                              <ToggleButton
                                key={ci2.CodedValue} value={ci2.CodedValue} selected={sel} size="small"
                                onClick={() => setBranches(prev => prev.map((b, i) => i !== bi ? b : {
                                  ...b, conditions: b.conditions.map((c, j) => j !== ci ? c : {
                                    ...c, values: sel
                                      ? c.values.filter(v => v !== ci2.CodedValue)
                                      : [...c.values, ci2.CodedValue],
                                  }),
                                }))}
                                sx={{ fontSize: 11, py: 0.25, px: 1 }}
                              >
                                {ci2.Decode.TranslatedText.text}
                              </ToggleButton>
                            );
                          })}
                        </Stack>
                      ) : (
                        <TextField
                          size="small"
                          value={String(cond.values[0] ?? '')}
                          onChange={e => setBranches(prev => prev.map((b, i) => i !== bi ? b : {
                            ...b, conditions: b.conditions.map((c, j) => j === ci
                              ? { ...c, values: [e.target.value] }
                              : c),
                          }))}
                          sx={{ width: 100, '& input': { fontSize: 12 } }}
                        />
                      )}
                      {br.conditions.length > 1 && (
                        <IconButton size="small" color="error"
                          onClick={() => setBranches(prev => prev.map((b, i) => i !== bi
                            ? b
                            : { ...b, conditions: b.conditions.filter((_, j) => j !== ci) }))}>
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}
                <Button
                  size="small" startIcon={<AddIcon />}
                  sx={{ fontSize: 11, py: 0.25 }}
                  onClick={() => setBranches(prev => prev.map((b, i) => i !== bi
                    ? b
                    : { ...b, conditions: [...b.conditions, { field: '', op: 'eq', values: [], connector: 'AND' }] }))}
                >
                  Add condition
                </Button>
              </Box>

              {!removeMode && (
              <Box sx={{ pl: 1, borderLeft: 2, borderColor: 'success.light' }}>
                <Typography variant="caption" sx={{
                  display: 'block', fontWeight: 600,
                  color: 'success.dark', textTransform: 'uppercase', mb: 0.5,
                }}>
                  Then
                </Typography>
                {/* A branch outcome is not always a number. The generator passes formulaTemplate
                    through verbatim, so the literals `true` and `null` have always been emittable —
                    they were just undiscoverable behind a box labelled "formula", and reading one
                    back showed the author the word `true` with no explanation of what it did. */}
                <Select
                  size="small"
                  value={branchOutcome(br.formulaTemplate)}
                  onChange={e => {
                    const next = e.target.value as BranchOutcome;
                    setBranches(prev => prev.map((b, i) => i !== bi ? b : {
                      ...b,
                      formulaTemplate: next === 'value'
                        ? (branchOutcome(b.formulaTemplate) === 'value' ? b.formulaTemplate : '')
                        : next,
                      formulaVars: next === 'value' ? b.formulaVars : {},
                    }));
                  }}
                  sx={{ fontSize: 12, mb: 0.75, minWidth: 220 }}
                >
                  <MenuItem value="value">set the quantity to…</MenuItem>
                  <MenuItem value="true">remove the task</MenuItem>
                  <MenuItem value="null">leave the task unchanged</MenuItem>
                </Select>
                {branchOutcome(br.formulaTemplate) === 'value' && (<>
                <Stack direction="row" spacing={0.25} flexWrap="wrap" sx={{ mb: 0.5 }}>
                  {FORMULA_OPERATORS.map(op => (
                    <Button
                      key={op.label} size="small" title={op.title}
                      onClick={() => setBranches(prev => prev.map((b, i) => i !== bi
                        ? b
                        : { ...b, formulaTemplate: (b.formulaTemplate ?? '') + op.insert }))}
                      sx={{ minWidth: 32, fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {op.label}
                    </Button>
                  ))}
                </Stack>
                <TextField
                  size="small" fullWidth
                  value={br.formulaTemplate}
                  onChange={e => setBranches(prev => prev.map((b, i) => i !== bi
                    ? b
                    : { ...b, formulaTemplate: e.target.value }))}
                  placeholder="e.g. a + b"
                  inputProps={{ style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
                  sx={{ mb: 0.5 }}
                />
                {brVars.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {brVars.map(v => (
                      <Stack key={v} direction="row" spacing={0.25} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark', fontFamily: 'JetBrains Mono, monospace' }}>
                          {v}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>=</Typography>
                        <Select
                          size="small" displayEmpty
                          value={br.formulaVars[v] ?? ''}
                          onChange={e => setBranches(prev => prev.map((b, i) => i !== bi
                            ? b
                            : { ...b, formulaVars: { ...b.formulaVars, [v]: (e.target.value as OID) || null } }))}
                          sx={{ minWidth: 140, fontSize: 11 }}
                        >
                          <MenuItem value="">— Q —</MenuItem>
                          {numericItems.map(i => (
                            <MenuItem key={i.OID} value={i.OID}>{i.Question.TranslatedText.text}</MenuItem>
                          ))}
                        </Select>
                      </Stack>
                    ))}
                  </Stack>
                )}
                </>)}
              </Box>
              )}
            </Card>
          );
        })}
      </Stack>

      <Button
        size="small" variant="outlined" color="secondary"
        startIcon={<AddIcon />} sx={{ mt: 1 }}
        onClick={() => setBranches(prev => [...prev, {
          conditions: [{ field: '', op: 'eq', values: [] }],
          formulaTemplate: '',
          formulaVars: {},
        }])}
      >
        {removeMode ? 'Add another condition set' : 'Add ELSE IF branch'}
      </Button>

      {!removeMode && (
      <Card variant="outlined" sx={{ mt: 1.5, p: 1, bgcolor: 'background.paper' }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
          Otherwise
        </Typography>
        <Select
          size="small"
          value={branchOutcome(defaultValue)}
          onChange={e => {
            const next = e.target.value as BranchOutcome;
            setDefaultValue(next === 'value'
              ? (branchOutcome(defaultValue) === 'value' ? defaultValue : '0')
              : next);
          }}
          sx={{ fontSize: 12, mb: 0.75, minWidth: 220 }}
        >
          <MenuItem value="value">set the quantity to…</MenuItem>
          <MenuItem value="true">remove the task</MenuItem>
          <MenuItem value="null">leave the task unchanged</MenuItem>
        </Select>
        {branchOutcome(defaultValue) === 'value' && (
          <TextField
            size="small" fullWidth
            value={defaultValue}
            onChange={e => setDefaultValue(e.target.value)}
            placeholder="0"
            inputProps={{ style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
          />
        )}
      </Card>
      )}
    </Card>
  );
}

